import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { X, Upload, Download, AlertCircle, CheckCircle } from 'lucide-react';
import { addHolding } from '../api/portfolio';

interface ImportCSVModalProps {
  onClose: () => void;
}

interface ParsedHolding {
  symbol: string;
  shares: number;
  purchasePrice?: number;
  purchaseDate?: number;
  notes?: string;
  error?: string;
}

export default function ImportCSVModal({ onClose }: ImportCSVModalProps) {
  const [parsedData, setParsedData] = useState<ParsedHolding[]>([]);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState<{ success: number; failed: number } | null>(null);
  const queryClient = useQueryClient();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      parseCSV(selectedFile);
    }
  };

  const parseCSV = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        alert('CSV file is empty or invalid');
        return;
      }

      // Parse header
      const header = lines[0].toLowerCase().split(',').map(h => h.trim());
      
      // Find column indices
      const symbolIdx = header.findIndex(h => h.includes('symbol') || h.includes('ticker'));
      const sharesIdx = header.findIndex(h => h.includes('share') || h.includes('quantity') || h.includes('amount'));
      const priceIdx = header.findIndex(h => h.includes('price') || h.includes('cost'));
      const dateIdx = header.findIndex(h => h.includes('date') || h.includes('purchase'));
      const notesIdx = header.findIndex(h => h.includes('note') || h.includes('comment'));

      if (symbolIdx === -1 || sharesIdx === -1) {
        alert('CSV must have at least "Symbol" and "Shares" columns');
        return;
      }

      // Parse data rows
      const parsed: ParsedHolding[] = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        
        const symbol = values[symbolIdx]?.toUpperCase();
        const shares = parseFloat(values[sharesIdx]);
        
        if (!symbol || isNaN(shares) || shares <= 0) {
          parsed.push({
            symbol: symbol || 'UNKNOWN',
            shares: shares || 0,
            error: 'Invalid symbol or shares'
          });
          continue;
        }

        const holding: ParsedHolding = {
          symbol,
          shares,
        };

        // Optional fields
        if (priceIdx !== -1 && values[priceIdx]) {
          const price = parseFloat(values[priceIdx].replace('$', ''));
          if (!isNaN(price)) holding.purchasePrice = price;
        }

        if (dateIdx !== -1 && values[dateIdx]) {
          const date = new Date(values[dateIdx]);
          if (!isNaN(date.getTime())) holding.purchaseDate = date.getTime();
        }

        if (notesIdx !== -1 && values[notesIdx]) {
          holding.notes = values[notesIdx];
        }

        parsed.push(holding);
      }

      setParsedData(parsed);
    };

    reader.readAsText(file);
  };

  const handleImport = async () => {
    setImporting(true);
    let success = 0;
    let failed = 0;

    for (const holding of parsedData) {
      if (holding.error) {
        failed++;
        continue;
      }

      try {
        await addHolding(
          holding.symbol,
          holding.shares,
          holding.purchasePrice,
          holding.purchaseDate,
          holding.notes
        );
        success++;
      } catch (error) {
        console.error(`Failed to import ${holding.symbol}:`, error);
        failed++;
      }
    }

    setImportResults({ success, failed });
    setImporting(false);
    queryClient.invalidateQueries({ queryKey: ['portfolio'] });
  };

  const downloadTemplate = () => {
    const template = `Symbol,Shares,Purchase Price,Purchase Date,Notes
AAPL,10,150.00,2024-01-15,Long-term hold
MSFT,5,380.00,2024-02-20,Tech diversification
SPY,20,450.00,2024-03-10,Index fund`;

    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'portfolio_template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="card max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Import Portfolio from CSV</h2>
            <p className="text-sm text-gray-600 mt-1">Upload a CSV file with your holdings</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Instructions */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-semibold text-blue-900 mb-2">📋 CSV Format</h3>
          <div className="text-sm text-blue-800 space-y-1">
            <p><strong>Required columns:</strong> Symbol, Shares</p>
            <p><strong>Optional columns:</strong> Purchase Price, Purchase Date, Notes</p>
            <p className="text-xs text-blue-600 mt-2">Column names are flexible (e.g., "Ticker" works for "Symbol")</p>
          </div>
          <button
            onClick={downloadTemplate}
            className="mt-3 btn btn-secondary text-sm flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Download Template
          </button>
        </div>

        {/* File Upload */}
        {!parsedData.length && !importResults && (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <label className="cursor-pointer">
              <span className="btn btn-primary">Choose CSV File</span>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="hidden"
              />
            </label>
            <p className="text-sm text-gray-500 mt-2">or drag and drop</p>
          </div>
        )}

        {/* Preview Parsed Data */}
        {parsedData.length > 0 && !importResults && (
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">
              Preview ({parsedData.length} holdings found)
            </h3>
            <div className="border border-gray-200 rounded-lg overflow-hidden mb-4">
              <div className="max-h-64 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Symbol</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Shares</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Price</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {parsedData.map((holding, idx) => (
                      <tr key={idx} className={holding.error ? 'bg-red-50' : ''}>
                        <td className="px-3 py-2 font-medium">{holding.symbol}</td>
                        <td className="px-3 py-2 text-right">{holding.shares.toFixed(4)}</td>
                        <td className="px-3 py-2 text-right">
                          {holding.purchasePrice ? `$${holding.purchasePrice.toFixed(2)}` : '—'}
                        </td>
                        <td className="px-3 py-2">
                          {holding.purchaseDate ? new Date(holding.purchaseDate).toLocaleDateString() : '—'}
                        </td>
                        <td className="px-3 py-2">
                          {holding.error ? (
                            <span className="flex items-center gap-1 text-red-600">
                              <AlertCircle className="w-4 h-4" />
                              <span className="text-xs">{holding.error}</span>
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-green-600">
                              <CheckCircle className="w-4 h-4" />
                              <span className="text-xs">Ready</span>
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setParsedData([]);
                }}
                className="btn btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={handleImport}
                disabled={importing || parsedData.every(h => h.error)}
                className="btn btn-primary flex-1"
              >
                {importing ? 'Importing...' : `Import ${parsedData.filter(h => !h.error).length} Holdings`}
              </button>
            </div>
          </div>
        )}

        {/* Import Results */}
        {importResults && (
          <div className="text-center">
            <div className="mb-4">
              {importResults.success > 0 && (
                <div className="flex items-center justify-center gap-2 text-green-600 mb-2">
                  <CheckCircle className="w-8 h-8" />
                  <span className="text-2xl font-bold">{importResults.success}</span>
                  <span className="text-lg">imported successfully</span>
                </div>
              )}
              {importResults.failed > 0 && (
                <div className="flex items-center justify-center gap-2 text-red-600">
                  <AlertCircle className="w-6 h-6" />
                  <span className="text-lg">{importResults.failed} failed</span>
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              className="btn btn-primary"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
